import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { RiArrowGoBackLine } from 'react-icons/ri'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { generateUUID } from '@renderer/utils/idUtils'
import cogoToast from '@successtar/cogo-toast'
import PromoModal from '@renderer/components/samu/PromoModal'
import { FaTicket } from 'react-icons/fa6'
import { FaQuestionCircle } from 'react-icons/fa'

const Welcome: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const [showPromoModal, setShowPromoModal] = useState(false)
  const [showTutorial, setShowTutorial] = useState(false)
  const [discount, setDiscount] = useState(0)
  const { setSessionState, uploadSessionState } = useSessionStateStore()
  const { getPageSettings } = useConfigStore()
  const welcomeSettings = getPageSettings('welcome')
  const [isSessionStarting, setIsSessionStarting] = useState(false)

  const startSession = async (): Promise<void> => {
    if (isSessionStarting) {
      return // Prevent multiple calls
    }
    setIsSessionStarting(true)

    try {
      // Credit deduction is now handled in Capture.tsx when capture session starts
      // This ensures customers only pay when they actually start capturing

      const sessionId = generateUUID()
      setSessionState({
        id: sessionId,
        projectId,
        mode: 'capture',
        timeline: {
          startTime: new Date(),
          activityLogs: [
            {
              page: 'welcome',
              type: 'session',
              action: 'start',
              details: { mode: 'capture', discount: 0 },
              timestamp: new Date()
            }
          ]
        },
        userPreferences: {
          useGIF: getPageSettings('capture')?.gifEnabled,
          useRecording: getPageSettings('capture')?.recordingEnabled,
          useVideo: getPageSettings('record')?.enabled
        },
        gallery: {
          id: `${9999999999999 - Date.now()}-${sessionId}`
        },
        transaction: {
          discount: discount
        }
      })

      const nextPage = welcomeSettings?.startWithPayment ? 'checkout' : 'filter'
      uploadSessionState()
      navigate(`/${projectId}/${nextPage}`)
    } catch (error) {
      console.error('Error starting session:', error)
      cogoToast.error('Error starting session')
    } finally {
      setIsSessionStarting(false)
    }
  }

  const handlePromoApplied = (discountPercentage: number): void => {
    setDiscount(discountPercentage)
    setSessionState({
      transaction: {
        discount: discountPercentage
      }
    })
  }

  return (
    <div
      className="relative w-full h-full bg-cover bg-center"
      style={{
        backgroundImage: showTutorial
          ? 'url(/assets/samu/samu-tutorial.webp)'
          : 'url(/assets/samu/samu-welcome.webp)'
      }}
    >
      <div className="absolute z-[5] left-[130px] top-[120px]">
        {showTutorial && (
          <button
            onClick={() => setShowTutorial(false)}
            className="flex items-center gap-3 rounded-full bg-carnabyTan px-6 py-3 text-juicyMango hover:opacity-90 transition-opacity"
          >
            <RiArrowGoBackLine size={36} />
            <span className="font-body-samu text-3xl">Back</span>
          </button>
        )}
      </div>

      {!showTutorial && (
        <>
          <div className="absolute bottom-[100px] right-[105px] w-[380px] flex flex-col gap-4">
            <button
              onClick={() => setShowTutorial(true)}
              className="w-full font-body-samu flex items-center gap-4 rounded-lg hover:brightness-110 bg-carnabyTan text-white px-20 tracking-widest text-2xl py-4 transition-transform transform shadow-xl active:brightness-110"
            >
              <FaQuestionCircle /> Read Tutorial
            </button>
            <button
              onClick={startSession}
              className="w-full font-body-samu rounded-lg hover:brightness-110 bg-carnabyTan text-white px-20 tracking-widest text-5xl py-8 transition-transform transform shadow-xl active:brightness-110"
            >
              START
            </button>
          </div>

          <div className="absolute bottom-[120px] left-[105px] w-[320px] flex flex-col items-center">
            <button
              onClick={() => setShowPromoModal(true)}
              className="w-full font-body-samu flex items-center gap-4 px-4 rounded-lg hover:brightness-110 bg-carnabyTan text-white tracking-widest text-2xl py-4 transition-transform transform shadow-xl active:brightness-110"
            >
              <FaTicket /> Enter Promo Code
            </button>
          </div>
        </>
      )}

      {showPromoModal && (
        <PromoModal
          showModal={showPromoModal}
          setShowModal={setShowPromoModal}
          onPromoApplied={handlePromoApplied}
        />
      )}
    </div>
  )
}

export default Welcome
